import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../components/Config/api.service';
import { CompanyInfo } from '../../components/Setup/CompanyInfo';
import { EmployeeCountStep } from '../../components/Setup/EmployeeCount';
import Documents from '../Employee/Profil/Documents';
import { AdminInfo } from '../../components/Setup/AdminInfo';
import { CompanyDocuments } from '../../components/Setup/CompanyDocuments';

const MultiStepRegistration = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const savedCode = typeof window !== 'undefined' ? localStorage.getItem('selectedCountryCode') : null;
    const savedLabel = typeof window !== 'undefined' ? localStorage.getItem('selectedCountryLabel') : null;

    const [currentStep, setCurrentStep] = useState(2); // registration assumes country already selected
    const [formData, setFormData] = useState({
        companyName: '',
        legalForm: '',
        companyId: '',
        companyAddress: '',
        city: '',
        sector: '',
        hourlyRate: '',
        termsAccepted: false,
        country: savedCode || 'sn',
        employeeCount: '10-30',
        payrollManagement: 'no-payslip',
        companyLogo: null,
        administrativeDoc: null,
        lastName: '',
        firstName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        position: ''
    });
    const [countryLabel, setCountryLabel] = useState(savedLabel || '');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const c = localStorage.getItem('selectedCountryCode');
        const l = localStorage.getItem('selectedCountryLabel');
        if (!c) {
            navigate('/countryselect', { replace: true });
            return;
        }
        if (c && c !== formData.country) {
            setFormData(fd => ({ ...fd, country: c }));
        }
        if (l) setCountryLabel(l);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const totalSteps = 5;

    const handleInputChange = (field, value) => {
        setFormData({
            ...formData,
            [field]: value
        });
    };

    const nextStep = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
            window.scrollTo(0, 0);
        } else {
            handleSubmit();
        }
    };

    const previousStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            window.scrollTo(0, 0);
        }
    };

    const goToCountrySelection = () => {
        navigate('/countryselect');
    };

    const handleSubmit = async () => {
        setErrorMessage('');
        setSuccessMessage('');
        setIsLoading(true);
        try {
            const apiData = {
                company_name: formData.companyName,
                company_address: formData.companyAddress,
                employeeCount: formData.employeeCount,
                country: formData.country,
                username: `${formData.firstName} ${formData.lastName}`,
                email: formData.email,
                password: formData.password
            };

            const response = await apiService.register(apiData);
            setSuccessMessage(t('inscription.accountCreatedSuccess'));
            setTimeout(() => setIsLoading(false), 1000);
        } catch (error) {
            console.error('Erreur lors de l\'inscription:', error);
            setErrorMessage(t('inscription.creationError'));
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 2:
                return <CompanyInfo formData={formData} onChange={handleInputChange} />;
            case 3:
                return <EmployeeCountStep formData={formData} onChange={handleInputChange} />;
            case 4:
                return <CompanyDocuments formData={formData} onChange={handleInputChange} />;
            case 5:
                return <AdminInfo formData={formData} onChange={handleInputChange} isLoading={isLoading} />;
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-[--bg-color]">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 px-8 py-6 border-b border-gray-200 content-center">
                <div className='text-center text-4xl text-[--color-primary] font-extrabold'> YROS </div>
                <div className="flex justify-between items-center">
                    {[...Array(totalSteps)].map((_, index) => (
                        <React.Fragment key={index}>
                            <div className="w-3 h-3 rounded-full transition-all duration-300
                                ${index + 1 === currentStep ? 'bg-[--color-primary]' : index + 1 < currentStep ? 'bg-green-500' : 'bg-gray-200'}"></div>
                            {index < totalSteps - 1 && (
                                <div className="relative flex-1 h-1 mx-2">
                                    <div className="absolute w-full h-full bg-gray-200 rounded"></div>
                                    <div
                                        className="absolute h-full bg-[--color-primary] rounded transition-all duration-500"
                                        style={{ width: index + 1 < currentStep ? '100%' : '0%' }}
                                    />
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
                <div className='text-center text-xl'>
                    <span className='text-gray-500'> {t('inscription.haveQuestion')} </span>
                    <span className='text-[--color-orange]'> {t('aboutPage.contactUs')} </span>
                </div>
            </div>

            <div className="container mx-auto px-4 py-12 flex-1">
                <div className="max-w-4xl mx-auto bg-white rounded-xl overflow-hidden">
                    <div className="p-8">
                        <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 rounded flex items-center justify-between">
                            <div>
                                <strong className="block">{t('inscription.selectedCountry')} {countryLabel || formData.country?.toUpperCase()}</strong>
                                {/* <span className="text-sm block">Le choix du pays est essentiel — il détermine la base de données utilisée.</span> */}
                            </div>
                            <div>
                                <button onClick={goToCountrySelection} className="ml-4 bg-white border border-yellow-400 text-yellow-700 px-4 py-2 rounded">
                                    {t('inscription.changeCountry')}
                                </button>
                            </div>
                        </div>

                        {errorMessage && (
                            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                                {errorMessage}
                            </div>
                        )}

                        {isLoading ? (
                            <div className="flex justify-center py-8">
                                <div className="relative w-16 h-16">
                                    <div className="absolute top-0 w-full h-full border-4 border-gray-200 rounded-full"></div>
                                    <div className="absolute top-0 w-full h-full border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                                </div>
                            </div>
                        ) : (successMessage ? (
                            <div className="mt-8 mb-12 text-center">
                                <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('inscription.accountSetupSuccess')}</h2>
                                <p className="text-gray-600 mb-8">{t('inscription.canNowLogin')}</p>
                                <div className="flex justify-center">
                                    <button onClick={() => window.location.href = '/'} className="bg-[--color-primary] text-white px-8 py-3 rounded-lg">
                                        {t('companyInfoSetup.logIn')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <Fragment>{renderStep()}</Fragment>
                        ))}

                        {!successMessage && (
                            <div className="flex justify-between mt-6">
                                <button
                                    type="button"
                                    className="border btn-outline text-[--color-primary] px-8 py-2 rounded-lg"
                                    onClick={previousStep}
                                    disabled={currentStep === 2}
                                >
                                    {t('dailySalaryInputs.previous')}
                                </button>
                                <button
                                    type="button"
                                    className="bg-[--color-primary] text-white px-8 py-2 rounded-lg"
                                    onClick={nextStep}
                                >
                                    {currentStep === totalSteps ? t('inscription.finish') : t('dailySalaryInputs.next')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MultiStepRegistration;